import {
	type Notification as NotificationType,
	useNotifications,
} from "@novu/react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInMonths,
} from "date-fns";
import DOMPurify from "dompurify";
import { motion } from "motion/react";
import { Popover } from "radix-ui";
import { useEffect, useState } from "react";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/utils";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";

function Notification({ notification }: { notification: NotificationType }) {
	const navigate = useNavigate();
	const [isHovered, setIsHovered] = useState(false);
	const { track } = useAnalytics();

	const distance = (date: Date) => {
		const minutes = differenceInMinutes(new Date(), date);

		if (minutes < 3) {
			return `Agora mesmo`;
		} else if (minutes < 60) {
			return `${minutes} minutos atrás`;
		}

		const hours = differenceInHours(new Date(), date);

		if (hours < 24) {
			return hours === 1 ? "1 hora atrás" : `${hours} horas atrás`;
		}

		const days = differenceInDays(new Date(), date);

		if (days < 30) {
			return days === 1 ? "1 dia atrás" : `${days} dias atrás`;
		}

		const months = differenceInMonths(new Date(), date);

		if (months < 1) {
			return days === 1 ? "1 dia atrás" : `${days} dias atrás`;
		}

		return months === 1 ? "1 mês atrás" : `${months} meses atrás`;
	};

	const content = DOMPurify.sanitize(notification.subject || notification.body);

	return (
		<div className="border-b py-1 flex-1 w-full">
			{/*biome-ignore lint/a11y/noStaticElementInteractions: no explanation
		biome-ignore lint/a11y/useKeyWithClickEvents: no explanation*/}
			<div
				className={cn(
					"flex flex-row items-center py-3 gap-3 relative rounded px-1 max-w-full overflow-x-hidden",
					!notification.isSeen && "bg-accent/30",
					notification.redirect &&
						"cursor-pointer hover:bg-accent/50 hover:text-accent-foreground",
				)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={
					notification.redirect?.url
						? () => {
								track(AnalyticsEvents.NOTIFICATION_CLICKED, {
									notification_id: notification.id,
								});
								if (!notification.isSeen) {
									notification.read();
								}
								navigate({ href: notification.redirect?.url });
							}
						: undefined
				}
			>
				<div className="size-8 aspect-square text-accent-foreground relative rounded-full bg-accent flex items-center justify-center">
					<Icon name="MessageCircle" className="size-4" />
				</div>

				{/*<div>*/}
				<div className="flex-1 overflow-x-hidden flex flex-col items-start justify-start">
					<div className="flex-1 line-clamp-3">
						<div
							// biome-ignore lint/security/noDangerouslySetInnerHtml: is sanitized
							dangerouslySetInnerHTML={{
								__html:
									content.length > 100
										? `${content?.slice(0, 100)}...`
										: content,
							}}
							className="w-full overflow-hidden flex flex-wrap line-clamp-3"
						/>
					</div>

					<span className="text-xs/5 text-muted-foreground">
						{distance(new Date(notification.createdAt))}
					</span>
				</div>

				{isHovered && notification.isSeen && (
					<div
						className={cn(
							"h-full absolute px-3 right-0 flex items-center justify-center",
						)}
					>
						<motion.button
							type="button"
							className="size-8 flex bg-destructive hover:scale-105 items-center justify-center rounded-full text-destructive-foreground"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={(ev) => {
								ev.stopPropagation();

								notification.delete();
							}}
						>
							<Icon name="Trash2" className="size-4" />
						</motion.button>
					</div>
				)}
			</div>
		</div>
	);
}

export function NotificationWindow({ open }: { open: boolean }) {
	const { notifications } = useNotifications();

	const pendingNotifications = notifications?.filter((n) => !n.isRead) || [];

	const { track: trackNotif } = useAnalytics();

	useEffect(() => {
		if (open) {
			trackNotif(AnalyticsEvents.NOTIFICATION_OPENED, {});
			const timeout = setTimeout(() => {
				pendingNotifications.forEach((not) => {
					not.read();
				});
			}, 5000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [open, pendingNotifications.forEach, trackNotif]);

	return (
		<Popover.Portal>
			<Popover.Content className="z-2">
				<Card className="w-[400px] overflow-y-auto max-h-[500px] no-scrollbar">
					<CardHeader className="flex flex-row items-center justify-between">
						<CardTitle>
							Notificações{" "}
							{pendingNotifications.length
								? `(${pendingNotifications.length})`
								: null}
						</CardTitle>

						<Link to="/notificacoes/configuracoes">
							<Button variant="ghost">
								<Icon name="Settings" className="size-4" />
							</Button>
						</Link>
					</CardHeader>

					<CardContent>
						{notifications?.map((not) => (
							<Notification key={not.id} notification={not} />
						))}
					</CardContent>
				</Card>
			</Popover.Content>
		</Popover.Portal>
	);
}
